import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Shield, X as XIcon } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import { useToastContext } from '../../context/ToastContext';
import { organizationRolesAPI } from '../../api/endpoints';

const PERMISSIONS = [
  { id: 'manage_attendees', label: 'Manage Attendees (Check-in/out)' },
  { id: 'view_analytics', label: 'View Analytics' },
  { id: 'manage_events', label: 'Manage Events' },
  { id: 'manage_fields', label: 'Manage Custom Fields' },
  { id: 'manage_badges', label: 'Manage Badges' },
];

const RoleModal = ({ isOpen, onClose, role = null }) => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [formData, setFormData] = useState({
    name: role?.name || '',
    permissions: role?.permissions || {}
  });

  const isSystem = role?.is_system;

  const createMutation = useMutation({
    mutationFn: organizationRolesAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationRoles'] });
      toast.success('Role created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create role');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => organizationRolesAPI.update(role.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationRoles'] });
      toast.success('Role updated successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (role) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePermissionToggle = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permId]: !prev.permissions[permId]
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">{role ? 'Edit Role' : 'Create Role'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              disabled={isSystem}
              placeholder="e.g. Event Manager"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="space-y-2 border rounded-md p-3 max-h-60 overflow-y-auto">
                {PERMISSIONS.map(perm => (
                  <label key={perm.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={!!formData.permissions[perm.id]}
                      onChange={() => handlePermissionToggle(perm.id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                {role ? 'Save Changes' : 'Create Role'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const OrganizationRolesTab = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['organizationRoles'],
    queryFn: async () => {
      const response = await organizationRolesAPI.getAll();
      return response.data.data;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: organizationRolesAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationRoles'] });
      toast.success('Role deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this role? Users with this role must be reassigned first.')) {
      deleteMutation.mutate(id);
    }
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const openCreate = () => {
    setEditingRole(null);
    setIsModalOpen(true);
  };

  if (isLoading) return <div className="p-4 text-center">Loading roles...</div>;

  const roles = data || [];

  return (
    <Card>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Roles & Permissions</h3>
            <p className="text-sm text-gray-500">Manage custom roles and define their capabilities.</p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Create Role
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map(role => (
                <tr key={role.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Shield className={`w-5 h-5 mr-2 ${role.is_system ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <span className="font-medium text-gray-900">{role.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${role.is_system ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'}`}>
                      {role.is_system ? 'System Default' : 'Custom'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(role.permissions || {})
                        .filter(([_, val]) => val)
                        .map(([key]) => {
                          const p = PERMISSIONS.find(p => p.id === key);
                          return (
                            <span key={key} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                              {p?.label || key}
                            </span>
                          );
                        })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => openEdit(role)} className="text-indigo-600 hover:text-indigo-900 mr-3">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!role.is_system && (
                      <button onClick={() => handleDelete(role.id)} className="text-red-600 hover:text-red-900">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isModalOpen && (
          <RoleModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            role={editingRole}
          />
        )}
      </div>
    </Card>
  );
};

export default OrganizationRolesTab;
